import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Grid,
  Divider
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthProfile } from '../../app/hooks';

const imageUrl = "/map/preview/ny2026/image.jpg";

const CommunityResolutionsPage = () => {
    const { authStatus, profile: loggedInUserProfile, isLoading: isLoadingLoggedInUserProfile, isSuccess: isSuccessLoggedInUserProfile, isError: isErrorLoggedInUserProfile, error: errorLoggedInUserProfile } = useAuthProfile()
      const [params, setSearchParams] = useSearchParams();
      const t = params.get("t");    
      const userSignIn = params.get("userSignIn") === 'true'
      const noLoginPrompt = params.get("nlp") === 'true'
      const phooneNumberToLink = params.get("phone");
      const conversationId = params.get("conversationId");
      const navigate = useNavigate()
  return (
    <Box 
      id='resolutions-root-box' 
      sx={{ 
        //display: 'flex', 
        //flexDirection: 'column', 
        //alignItems: 'center', 
        width: '100%',
        // Changed height to minHeight to prevent content clipping
        minHeight: { xs: '100%', md: '100%' }, 
        // Changed md: 'hidden' to 'auto' to enable laptop/desktop scrolling
        overflow: { xs: 'auto', md: 'auto' }, 
        bgcolor: '#fafafa'
      }}
    >
       <Stack >
      {/* Header Section */}
      <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 700, px: 2, pt: 3, mb: 0.5 }}>
        Multiple Myeloma Warrior Community 2026 New Year's Resolutions
      </Typography>
      <Typography variant="h8" sx={{ textAlign: 'center', px: 5, mb:  {  md: 1, lg:2 },   fontWeight: '700', color: '#919191ff' }}>
        Thank you for participating! View our community collage and a poem inspired by your responses.
      </Typography>
      </Stack>
      <Divider orientation="horizontal" flexItem />

      {/* Main Content Area */}
      <Box 
        sx={{
          width: '100%',
          flexGrow: 1,
          backgroundColor: 'white',
          p: { xs: 2, md: 0, xl:0 },
          // Ensure the inner box also allows scrolling if parent height is constrained
          overflowY: 'auto' 
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 0, xl:1 },
            mt: { xs: 0, md: 0, xl: 3 },
            
            borderRadius: 3,
            background: 'linear-gradient(135deg, #E5D4F5 0%, #F7DEE8 100%)',
            maxWidth: '1200px', // Prevents the 2-col from getting too wide on ultrawide screens
            margin: '0 auto'
          }}
        >
          <Grid container spacing={4} alignItems="flex-start">
            {/* Image Column */}
            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={imageUrl}
                alt="Community resolutions"
                sx={{
                  width: '100%',
                  maxWidth: { xs: '220px', md: '100%' },
                  height: 'auto',
                  display: 'block',
                  mt: {xs: 0, md: 2},
                   ml: {xs: 0, md: 2}
                }}
              />
            </Grid>

            {/* Poem Column - 2 Columns */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: {md: 1, xl:0} }}>
                One Community, Many Hopes
              </Typography>
              
              <Box 
                sx={{ 
                  columnCount: { xs: 1, sm: 2 },
                  columnGap: 4,
                  whiteSpace: 'pre-line',
                  '& .MuiTypography-root': {
                    fontSize: { xs: '0.85rem', md: '0.95rem' }, 
                    lineHeight: 1.5,
                    mb: 2,
                    breakInside: 'avoid-column' 
                  }
                }}
              >
                <Typography variant="body1">
                  This year, we choose strength—{"\n"}
                  not just in muscle,{"\n"}
                  but in mind, heart, and spirit.
                </Typography>

                <Typography variant="body1">
                  Some of us are lifting weights.{"\n"}
                  Some of us are lifting prayers.{"\n"}
                  Some are lifting hope{"\n"}
                  one quiet day at a time.
                </Typography>

                <Typography variant="body1">
                  We are eating better.{"\n"}
                  Sleeping more.{"\n"}
                  Moving 20 minutes.{"\n"}
                  Riding the bike—{"\n"}
                  25 minutes at a time.
                </Typography>

                <Typography variant="body1">
                  We are choosing joy.{"\n"}
                  Replacing stress with today.{"\n"}
                  Finding gratitude in small wins.{"\n"}
                  Writing it down{"\n"}
                  so we don’t forget.
                </Typography>

                <Typography variant="body1">
                  Some of us travel—{"\n"}
                  across states, across oceans,{"\n"}
                  or just away from the screen{"\n"}
                  and back to the people we love.
                </Typography>

                <Typography variant="body1">
                  Some of us say: No resolutions.{"\n"}
                  And that’s okay too.{"\n"}
                  Living is already work enough.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  One wish echoes through us all:{"\n"}
                  A cure.
                </Typography>

                <Typography variant="body1">
                  Until then,{"\n"}
                  we live our best lives—{"\n"}
                  fully, honestly,{"\n"}
                  together.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Footer Button */}
        <Stack 
          direction="column" 
          justifyContent="center" 
          alignItems="center" 
          spacing={2}
          sx={{ mt: {  md: 1, xl: 4 }, pb: 3 }} 
        >
          <Typography variant="body1" sx={{  mt: { sm: 3, md: 1, xl: 4 }, fontWeight: 600, textAlign: 'center' }}>
          {(authStatus === 'unauthenticated' && isSuccessLoggedInUserProfile) ?' Ready to join the community?':'Patient Connect' }
          </Typography>
          {(authStatus === 'unauthenticated' && isSuccessLoggedInUserProfile) && 
          <Button id="event2026-singup"
            variant="contained"
            disableElevation
            sx={{
              textTransform: 'none',
              borderRadius: 10,
              px: 6,
              py: 1,
              fontSize: '1rem',
              backgroundColor: "#5468FF",
              '&:hover': { backgroundColor: '#4353CC' }
            }}          
            onClick={()=> { 
               const singinParams = new URLSearchParams({  ...t?{t}:{} });
              navigate(`/SignInToMap?${singinParams.toString()}`)}}
          >
            Sign Up
          </Button>}
          <Typography variant="subtitle2" sx={{ textAlign: 'center', px: 5, mb:  {  md: 1, lg:2 }, fontWeight: { sm: '400', md: '700'}, color: '#919191ff' }}>
        Whether you are newly diagnosed, in remission, preparing for treatment or supporting someone you love, Patient Connect is here to help you navigate your journey - together.
      </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default CommunityResolutionsPage;